import React, { useState, useEffect, useCallback } from "react";
import { collection, getDocs } from "firebase/firestore";

import Navbar from "./components/shared/Navbar";
import Footer from "./components/shared/Footer";
import BottomTicker from "./components/shared/BottomTicker";
import EpisodeModal from "./components/public/EpisodeModal";
import Toast from "./components/shared/Toast";

import HomePage from "./pages/public/HomePage";
import EpisodesPage from "./pages/public/EpisodesPage";
import { BlogPage, BlogArticle } from "./pages/public/BlogPage";
import {
  GuestsPage,
  LivePage,
  AboutPage,
  ContactPage,
} from "./pages/public/OtherPages";

import { getYouTubeEmbedUrl } from "./lib/embeds";

export default function PublicSite({ db, theme, toggleTheme }) {
  const [page, setPage] = useState("home");
  const [blogPost, setBlogPost] = useState(null);
  const [modal, setModal] = useState(null);
  const [toast, setToast] = useState(null);

  const [episodes, setEpisodes] = useState([]);
  const [guests, setGuests] = useState([]);
  const [posts, setPosts] = useState([]);
  const [liveData, setLiveData] = useState({ isLive: false });
  const [aboutData, setAboutData] = useState({});

  const loadData = useCallback(() => {
    getDocs(collection(db, "episodes")).then((s) =>
      setEpisodes(
        s.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => b.createdAt - a.createdAt),
      ),
    );
    getDocs(collection(db, "guests")).then((s) =>
      setGuests(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
    getDocs(collection(db, "blogPosts")).then((s) =>
      setPosts(
        s.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((p) => p.status === "published")
          .sort((a, b) => b.createdAt - a.createdAt),
      ),
    );
    getDocs(collection(db, "settings")).then((s) => {
      const live = s.docs.find((d) => d.id === "live");
      const about = s.docs.find((d) => d.id === "about");
      if (live) setLiveData(live.data());
      if (about) setAboutData(about.data());
    });
  }, [db]);

  useEffect(() => {
    loadData();
  }, [page, loadData]);

  const navigate = (p) => {
    setPage(p);
    setBlogPost(null);
    window.scrollTo(0, 0);
  };

  const openBlogPost = (post) => {
    setBlogPost(post);
    window.scrollTo(0, 0);
  };

  return (
    <>
      <Navbar
        page={page}
        setPage={navigate}
        isLive={liveData.isLive}
        theme={theme}
        toggleTheme={toggleTheme}
      />

      <div style={{ paddingTop: "68px" }}>
        {page === "home" && (
          <HomePage
            episodes={episodes}
            liveData={liveData}
            aboutData={aboutData}
            posts={posts}
            setPage={navigate}
            setModal={setModal}
            setBlogPost={openBlogPost}
          />
        )}
        {page === "episodes" && (
          <EpisodesPage episodes={episodes} setModal={setModal} />
        )}
        {page === "blog" && !blogPost && (
          <BlogPage posts={posts} onOpenPost={openBlogPost} />
        )}
        {page === "blog" && blogPost && (
          <BlogArticle
            post={blogPost}
            onBack={() => {
              setBlogPost(null);
              window.scrollTo(0, 0);
            }}
          />
        )}
        {page === "guests" && <GuestsPage guests={guests} />}
        {page === "live" && (
          <LivePage liveData={liveData} getYTEmbed={getYouTubeEmbedUrl} />
        )}
        {page === "about" && <AboutPage aboutData={aboutData} />}
        {page === "contact" && <ContactPage db={db} showToast={setToast} />}
      </div>

      <Footer setPage={navigate} aboutData={aboutData} />

      <EpisodeModal episode={modal} onClose={() => setModal(null)} />

      <BottomTicker db={db} />

      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </>
  );
}
